/**
 * Pick a readable text colour (near-black ink or white) for text sitting ON a
 * solid fill of `hex`.
 *
 * ⭐ Chosen by WCAG 2.x CONTRAST (owner decision, 2026-10-06). The old rule — a
 * BT.601 luma threshold of 150 — chose white on six of the seven domain colours,
 * and all six failed: white on Personal #ef4444 is 3.76:1. Ink wins whenever it
 * contrasts at least as well as white, which puts dark ink on every domain colour
 * (purple, the closest call, reaches 4.48:1 against white's 3.96:1).
 * ⚠️ App-wide on purpose: the domain tab pills, the Nourishment lens pills, the
 * domain cards' glyphs … every caller now gets the more legible colour.
 *
 * Non-hex inputs (CSS vars, rgb(...)) can't be measured → default to white.
 */
export const INK_ON_FILL = '#111827';
export const WHITE_ON_FILL = '#fff';

/** sRGB channels (0–255) of a #rgb / #rrggbb colour, or null. */
function parseHex(hex: string): [number, number, number] | null {
  if (!hex || hex[0] !== '#') return null;
  const h = hex.length === 4 ? hex.slice(1).split('').map((c) => c + c).join('') : hex.slice(1);
  if (h.length !== 6) return null;
  const rgb = [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16)) as [number, number, number];
  return rgb.some(Number.isNaN) ? null : rgb;
}

/** WCAG 2.x relative luminance (0 = black … 1 = white), or null for a non-hex input. */
export function relativeLuminance(hex: string): number | null {
  const rgb = parseHex(hex);
  if (!rgb) return null;
  const [r, g, b] = rgb.map((c) => {
    const s = c / 255;
    return s <= 0.04045 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG 2.x contrast ratio between two hex colours (1 … 21), or null. */
export function contrastRatio(a: string, b: string): number | null {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  if (la == null || lb == null) return null;
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

export function readableOn(hex?: string): string {
  if (!hex) return WHITE_ON_FILL;
  const ink = contrastRatio(hex, INK_ON_FILL);
  const white = contrastRatio(hex, '#ffffff');
  if (ink == null || white == null) return WHITE_ON_FILL;
  return ink >= white ? INK_ON_FILL : WHITE_ON_FILL;
}

/** `#rrggbb` → `r g b` (a CSS colour triplet, e.g. Hydration's `--hy-w`), or undefined for anything else. */
export function hexTriplet(hex: string): string | undefined {
  const m = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex.trim());
  return m ? m.slice(1).map(h => parseInt(h, 16)).join(' ') : undefined;
}
